import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import {
  BottomSheet,
  Icon,
  inkA,
  orange,
  PlainButton,
  tokens,
} from 'components/v3';
import { fieldTypeLabel } from '../../shared/fieldTypes';
import { useBuilder } from '../BuilderContext';
import { fieldName } from '../desktop/FieldCard';
import { duplicateFieldAction } from '../state/builderReducer';

// A field's ⋯ sheet on phones (plan §5.7, design: FieldSheet): [Edit] /
// [Move up, Move down] / [Add field above, Add field below, Duplicate] /
// [Delete], each group its own rounded box, then Cancel. Delete takes a
// second tap ("Tap again to delete “X”"). Moving keeps the sheet open so a
// field can go several places in a row; when a move reaches the top or the
// bottom, focus hops to the other Move row instead of dropping to the page.
// From the Edit pane, Edit is left out. A locked field (w9-plan §6.5) has
// no sheet, and a field next to a locked one can't move past it.

const ERROR_RED = '#ED4337';

const Row = styled(PlainButton)`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  min-height: 50px;
  padding: 8px 14px;
  box-sizing: border-box;
  font-size: 15px;
  font-weight: 500;
  text-align: left;
  color: ${(p) => p.$color};

  & + & {
    border-top: 1px solid ${(p) => p.$line};
  }

  &:hover:not(:disabled),
  &:active:not(:disabled) {
    background: ${orange(0.08)};
  }

  &:disabled {
    opacity: 0.35;
  }
`;

const CancelButton = styled(PlainButton)`
  width: 100%;
  min-height: 50px;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 700;
  text-align: center;
  background: ${(p) => p.$bg};
  color: ${(p) => p.$color};
`;

// → [[item]]: the design's groups, empty ones dropped.
export const fieldActionGroups = ({
  name,
  index,
  count,
  includeEdit,
  confirming,
  lockedAbove = false,
  lockedBelow = false,
}) =>
  [
    includeEdit ? [{ key: 'edit', label: 'Edit field', icon: 'pencil' }] : [],
    [
      {
        key: 'up',
        label: 'Move up',
        icon: 'arrow-up',
        disabled: index === 0 || lockedAbove,
      },
      {
        key: 'down',
        label: 'Move down',
        icon: 'arrow-down',
        disabled: index === count - 1 || lockedBelow,
      },
    ],
    [
      { key: 'above', label: 'Add field above', icon: 'plus' },
      { key: 'below', label: 'Add field below', icon: 'plus' },
      { key: 'duplicate', label: 'Duplicate', icon: 'copy' },
    ],
    [
      {
        key: 'delete',
        label: confirming ? `Tap again to delete “${name}”` : 'Delete field',
        icon: 'trash-2',
        danger: true,
      },
    ],
  ].filter((group) => group.length);

// After a move: the row to focus when the one just used is now disabled.
export const moveFocusAfter = (direction, index, count) => {
  if (direction === 'up' && index === 1) return 'down';
  if (direction === 'down' && index === count - 2) return 'up';
  return null;
};

const ActionGroups = ({ groups, onSelect, rowRefs }) => {
  const t = tokens(useTheme());
  return groups.map((group, i) => (
    <div
      key={group[0].key}
      style={{
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid ' + t.border,
        borderRadius: 12,
        overflow: 'hidden',
        marginBottom: i < groups.length - 1 ? 8 : 0,
      }}
    >
      {group.map((item) => {
        const color = item.danger ? ERROR_RED : t.text;
        return (
          <Row
            key={item.key}
            ref={(el) => {
              rowRefs.current[item.key] = el;
            }}
            disabled={!!item.disabled}
            onClick={() => onSelect(item.key)}
            $color={color}
            $line={t.line}
          >
            <Icon
              name={item.icon}
              size={18}
              color={item.danger ? ERROR_RED : t.faint}
            />
            <span style={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>
              {item.label}
            </span>
          </Row>
        );
      })}
    </div>
  ));
};

ActionGroups.propTypes = {
  groups: PropTypes.arrayOf(PropTypes.array).isRequired,
  onSelect: PropTypes.func.isRequired,
  rowRefs: PropTypes.shape({ current: PropTypes.object }).isRequired,
};

const FieldActionSheet = ({
  fieldId,
  includeEdit,
  onClose,
  onEdit,
  onInsert,
}) => {
  const theme = useTheme();
  const t = tokens(theme);
  const { state, dispatch, showToast, isLocked } = useBuilder();
  const locked = (id) => !!isLocked && isLocked(id);
  const [confirming, setConfirming] = useState(false);
  const rowRefs = useRef({});
  const pendingFocus = useRef(null);
  useEffect(() => setConfirming(false), [fieldId]);
  useEffect(() => {
    const key = pendingFocus.current;
    pendingFocus.current = null;
    const row = key && rowRefs.current[key];
    if (row && !row.disabled) row.focus();
  });

  const { fields } = state.working;
  const index = fields.findIndex((f) => f.id === fieldId);
  const field = index >= 0 ? fields[index] : null;
  if (!field || locked(field.id)) return null;
  const name = fieldName(field);
  const lockedAbove = index > 0 && locked(fields[index - 1].id);
  const lockedBelow = index < fields.length - 1 && locked(fields[index + 1].id);

  const move = (direction) => {
    pendingFocus.current = moveFocusAfter(direction, index, fields.length);
    dispatch({ type: 'MOVE_FIELD', id: field.id, direction });
  };

  const HANDLERS = {
    edit: () => {
      onClose();
      onEdit(field.id);
    },
    up: () => move('up'),
    down: () => move('down'),
    above: () => {
      onClose();
      onInsert({ mode: 'before', fieldId: field.id });
    },
    below: () => {
      onClose();
      onInsert({ mode: 'after', fieldId: field.id });
    },
    duplicate: () => {
      dispatch(duplicateFieldAction(field.id));
      onClose();
      showToast('Field duplicated');
    },
    delete: () => {
      if (!confirming) {
        setConfirming(true);
        return;
      }
      onClose();
      dispatch({ type: 'REMOVE_FIELD', id: field.id });
      showToast('Field deleted');
    },
  };

  return (
    <BottomSheet
      open
      onClose={onClose}
      title={name}
      subtitle={`${fieldTypeLabel(field)} · field ${index + 1} of ${
        fields.length
      }`}
      showClose={false}
      footer={
        <CancelButton onClick={onClose} $bg={inkA(theme, 0.06)} $color={t.text}>
          Cancel
        </CancelButton>
      }
    >
      <ActionGroups
        groups={fieldActionGroups({
          name,
          index,
          count: fields.length,
          includeEdit,
          confirming,
          lockedAbove,
          lockedBelow,
        })}
        onSelect={(key) => HANDLERS[key]()}
        rowRefs={rowRefs}
      />
    </BottomSheet>
  );
};

FieldActionSheet.propTypes = {
  fieldId: PropTypes.string,
  includeEdit: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onInsert: PropTypes.func.isRequired,
};

FieldActionSheet.defaultProps = { fieldId: null, includeEdit: true };

export default FieldActionSheet;
